import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Sector, Line } from 'recharts';
import dashboardService from '../services/dashboardService';
import clientService from '../services/clientService';
import ClientSelector from '../components/ClientSelector';
import AccountView from '../components/AccountView';
import CreditCardView from '../components/CreditCardView';
import BillingView from '../components/BillingView';
import CedularRentView from '../components/CedularRentView';
import JuridicaIncomeView from '../components/JuridicaIncomeView';
import JuridicaCostsView from '../components/JuridicaCostsView';
import JuridicaNetIncomeView from '../components/JuridicaNetIncomeView';

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
        {payload.Nombre_Cliente}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{`Total ${new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value)}`}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
        {`(Rate ${(percent * 100).toFixed(2)}%)`}
      </text>
    </g>
  );
};

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A434F3', '#F33434', '#34F3A4', '#F3A434'];
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [availableTabs, setAvailableTabs] = useState<any[]>([]);

  const allTabs = [
    { label: "Cuenta de Ahorros", dataKey: "savings_data", component: AccountView, moduleKey: "cuenta_ahorros" },
    { label: "Cuenta Corriente", dataKey: "checking_data", component: AccountView, moduleKey: "cuenta_corriente" },
    { label: "Tarjeta de Crédito", dataKey: "credit_card_data", component: CreditCardView, moduleKey: "tarjeta_credito" },
    { label: "Facturación", dataKey: "billing_data", component: BillingView, moduleKey: "facturacion" },
    { label: "Declaración Natural Renta Cedular", dataKey: "cedular_rent_data", component: CedularRentView, moduleKey: "renta_persona_natural" },
    { label: "Declaración Renta Jurídica Ingresos y Ganancias", dataKey: "juridica_income_data", component: JuridicaIncomeView, moduleKey: "renta_juridica" },
    { label: "Declaración de Renta Jurídica – Gastos y Costos", dataKey: "juridica_costs_data", component: JuridicaCostsView, moduleKey: "renta_juridica" },
    { label: "Declaración de Renta Jurídica – Renta Líquida", dataKey: "juridica_net_income_data", component: JuridicaNetIncomeView, moduleKey: "renta_juridica" },
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (selectedClient) {
        setLoading(true);
        setError(null);
        setDashboardData(null);
        try {
          const [dashboardResponse, availableModulesResponse] = await Promise.all([
            dashboardService.getDashboardData(selectedClient),
            dashboardService.getAvailableModules(selectedClient)
          ]);
          const dashboardData = dashboardResponse.data;
          const availableModules = availableModulesResponse.data;
          
          const filteredTabs = allTabs.filter(tab => availableModules[tab.moduleKey]);
          setAvailableTabs(filteredTabs);

          setDashboardData(dashboardData);
          setActiveTab(0);
        } catch (err) {
          setError('Error al cargar los datos del dashboard.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      } else {
        setDashboardData(null);
        setAvailableTabs([]);
      }
    };
    fetchDashboardData();
  }, [selectedClient]);

  const handleClientChange = (clientName: string | null) => {
    setSelectedClient(clientName);
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <ClientSelector selectedClient={selectedClient} onClientChange={handleClientChange} />

      <Paper sx={{ mt: 2, p: 2, height: 'calc(100vh - 270px)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : dashboardData ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="vistas del dashboard" variant="scrollable" scrollButtons="auto">
                {availableTabs.map((tab, index) => (
                  <Tab label={tab.label} key={index} />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ pt: 2, flexGrow: 1, overflowY: 'auto' }}>
              {availableTabs.map((tab, index) => {
                const Component = tab.component;
                return activeTab === index && <Component key={index} data={dashboardData[tab.dataKey]} title={tab.label} />;
              })}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>Seleccione un cliente para ver el dashboard.</Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default DashboardPage;